import { useMemo, useState } from "react";

// material-ui
import { Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

// project imports
import NavItem from "./NavItem";
import NavGroup from "./NavGroup";
import menuItems from "menu-items";
import useAuth from "hooks/useAuth";

import useConfig from "hooks/useConfig";
import { HORIZONTAL_MAX_ITEM, MenuOrientation } from "config";
import { useGetMenuMaster } from "api/menu";

// types
import { NavItemType } from "types/menu";

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

export default function Navigation() {
  const { menuOrientation } = useConfig();
  const { user } = useAuth();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));

  const [selectedID, setSelectedID] = useState<string | undefined>("");
  const [selectedItems, setSelectedItems] = useState<string | undefined>("");
  const [selectedLevel, setSelectedLevel] = useState<number>(0);

  const isHorizontal =
    menuOrientation === MenuOrientation.HORIZONTAL && !downLG;
  const role = String(user?.positionKey || "").toLowerCase();
  const isAdmin = role === "admin";

  const visibleMenuItems = useMemo(() => {
    return menuItems.items
      .map((group) => {
        if (group.id === "group-pages" && !isAdmin) return null;
        if (group.id === "runway") {
          const nextChildren = (group.children || [])
            .map((child) => {
              if (child.id === "runway-tools" && !isAdmin) return null;
              if (!child.children || isAdmin) return child;

              const visibleChildren = child.children.filter(
                (item) =>
                  item.id !== "runway-edit-timeline" &&
                  item.id !== "runway-deterioration",
              );

              return {
                ...child,
                children: visibleChildren,
              };
            })
            .filter(Boolean) as NavItemType[];
          return {
            ...group,
            children: nextChildren,
          };
        }
        if (group.id === "administration") {
          const nextChildren = (group.children || []).map((child) => {
            if (child.id !== "administration-user-management") {
              return child;
            }
            return {
              ...child,
              children: (child.children || []).filter(
                (item) => item.id !== "administration-admin-setting" || isAdmin,
              ),
            };
          });
          return {
            ...group,
            children: nextChildren,
          };
        }
        return group;
      })
      .filter(Boolean) as NavItemType[];
  }, [isAdmin]);

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = visibleMenuItems.length - 1;
  let remItems: NavItemType[] = [];
  let lastItemId: string;

  //  first it checks menu item is more than giving HORIZONTAL_MAX_ITEM after that get lastItemid by giving horizontal max
  // item and it sets horizontal menu by giving horizontal max item lastly slice menuItem from array and set into remItems

  if (lastItem && lastItem < visibleMenuItems.length) {
    lastItemId = visibleMenuItems[lastItem - 1].id!;
    lastItemIndex = lastItem - 1;
    remItems = visibleMenuItems
      .slice(lastItem - 1, visibleMenuItems.length)
      .map((item) => ({
        title: item.title,
        elements: item.children,
        icon: item.icon,
        ...(item.url && {
          url: item.url,
        }),
      }));
  }

  const navGroups = visibleMenuItems
    .slice(0, lastItemIndex + 1)
    .map((item, index) => {
      switch (item.type) {
        case "group":
          if (item.url && item.id !== lastItemId) {
            return (
              <List
                key={item.id}
                sx={{ zIndex: 0, ...(isHorizontal && { mt: 0.5 }) }}
              >
                {!isHorizontal && index !== 0 && <Divider sx={{ my: 0.5 }} />}
                <NavItem
                  item={item}
                  level={1}
                  isParents
                  setSelectedID={setSelectedID}
                />
              </List>
            );
          }

          return (
            <NavGroup
              key={item.id}
              setSelectedID={setSelectedID}
              setSelectedItems={setSelectedItems}
              setSelectedLevel={setSelectedLevel}
              selectedLevel={selectedLevel}
              selectedID={selectedID}
              selectedItems={selectedItems}
              lastItem={lastItem!}
              remItems={remItems}
              lastItemId={lastItemId}
              item={item}
            />
          );
        default:
          return (
            <Typography key={item.id} variant="h6" color="error" align="center">
              Fix - Navigation Group
            </Typography>
          );
      }
    });

  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        ...(!isHorizontal && { "& > ul:first-of-type": { mt: 0 } }),
        display: isHorizontal ? { xs: "block", lg: "flex" } : "block",
      }}
    >
      {navGroups}
    </Box>
  );
}
